'use client';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { Field } from '../form/floating-label-field/field';

export const registerSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),

      gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: z.boolean().optional(),

      newsletter_return_url: z.string(),
      tos: z.boolean().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': z.string().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type RegisterFormProps = {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
};

export function RegisterForm({ initialSocialAuthProviders }: RegisterFormProps) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof registerSchema>>>({
    resolver: zodResolver(registerSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,
      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });

  const { toast } = useToast();
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {

      const resp = await register({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text653'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex w-full max-w-[430px] flex-col items-center justify-center gap-8 pt-6 sm:pt-8 lg:pt-16',
          'ea-register-form',
        )}
        onSubmit={handleSubmit}
      >
        {/* header */}
        <div className={cn('flex w-full flex-col gap-2.5', 'ea-register-form__header')}>
          <h3
            className={cn(
              'text-center text-3xl font-bold text-primary lg:text-4xl',
              'ea-register-form__header-title',
            )}
          >
            {t('text261')}
          </h3>

          <Link
            href={ROUTES.SIGN_IN() ?? '#'}
            className={cn('w-full text-center text-secondary', 'ea-register-form__link')}
            prefetch={false}
          >
            {t('text623')}
          </Link>
        </div>

        {/* content */}
        <SocialAuthButtons
          className="ea-register-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
          newsletterAccepted={form.watch('newsletter' as any)}
          newsletterReturnUrl={form.watch('newsletter_return_url' as any)}
        />

        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-8',
            'ea-register-form__content',
          )}
        >
          <Field
            control={form.control}
            name="name"
            label={t('text33')}
            type="text"
            required
            className={{ wrapper: 'ea-register-form__name-field col-span-12 w-full' }}
          />

          <Field
            control={form.control}
            name="surname"
            label={t('text34')}
            type="text"
            required
            className={{ wrapper: 'ea-register-form__surname-field col-span-12 w-full' }}
          />

          <Field
            control={form.control}
            name="email"
            label={t('text35')}
            type="email"
            required
            className={{ wrapper: 'ea-register-form__email-field col-span-12 w-full' }}
          />
          <Field
            control={form.control}
            name="phone"
            label={t('text36')}
            type="phone"
            required
            className={{ wrapper: 'ea-register-form__phone-field col-span-12 w-full' }}
          />

          <Field
            control={form.control}
            name="password"
            label={t('text191')}
            type="password"
            className={{ wrapper: 'ea-register-form__password-field col-span-12 w-full' }}
          />

          <Field
            control={form.control}
            name="password_confirmation"
            label={t('text263')}
            type="password"
            className={{
              wrapper: 'ea-register-form__password-confirmation-field col-span-12 w-full',
            }}
          />
        </div>
        {/* FOOTER */}
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-8',
            'ea-register-form__footer',
          )}
        >
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <FormField
              control={form.control}
              name="tos"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-start space-x-3 space-y-0',
                    'ea-register-form__tos-field',
                  )}
                >
                  <FormControl>
                    <Checkbox
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel>
                    {t('text264')}{' '}
                    <Link
                      href={ROUTES.HOME()}
                      className="underline"
                      prefetch={false}
                      target="_blank"
                    >
                      {t('text265')}
                    </Link>
                  </FormLabel>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gdpr"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-start space-x-3 space-y-0',
                    'ea-register-form__gdpr-field',
                  )}
                >
                  <FormControl>
                    <Checkbox
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel>
                    {t('text613')}&nbsp;
                    <Link
                      prefetch={false}
                      href={`${
                        locale === 'sk' ? '/ochrana-osobnych-udajov' : '/personal-data-protection'
                      }`}
                      className="underline"
                      target="_blank"
                    >
                      {t('text614')}
                    </Link>
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>

          {/* <FormField
            control={form.control}
            name="newsletter"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__newsletter-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="border-none outline outline-1 outline-border"
                  />
                </FormControl>
                <FormLabel>{t('text629')}</FormLabel>
              </FormItem>
            )}
          /> */}

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            variant={'secondary'}
            className={cn(
              'h-12 w-full text-sm lg:h-[50px] lg:text-base',
              'ea-register-form__submit-button',
            )}
            isLoading={form.formState.isSubmitting}
          >
            {t('text192')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
